import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal solicitando PIN de Gerencia para autorizar venta por debajo de precio_minimo.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - onAuthorized (autorizador)  -> callback con datos del autorizador para enviar al backend
 *  - violaciones [{producto, precio, precio_minimo}]
 */
export default function PinGerenciaModal({ open, onClose, onAuthorized, violaciones = [] }) {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    if (submitting) return;
    setPin("");
    onClose?.();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = (pin || "").trim();
    if (!/^\d{4,6}$/.test(clean)) {
      toast.error("El PIN debe tener entre 4 y 6 dígitos numéricos");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users/verify-pin-gerencia", { pin: clean });
      toast.success(`PIN validado por ${data.autorizador_nombre || data.autorizador_email}`);
      setPin("");
      onAuthorized?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg" data-testid="pin-gerencia-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#b53a25]">
            <ShieldAlert size={20} /> Autorización Gerencia · Precio bajo el mínimo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-[#fdecea] border border-[#d99584] p-3 text-sm text-[#7a2514]">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <AlertTriangle size={14} /> Las siguientes líneas violan el precio mínimo:
            </div>
            <ul className="text-xs list-disc pl-5 space-y-0.5">
              {violaciones.map((v, i) => (
                <li key={i}>
                  <span className="font-semibold">{v.producto}</span> ·
                  <span className="tabular-nums"> ${(+v.precio).toFixed(4)}</span> &lt; mín
                  <span className="tabular-nums"> ${(+v.precio_minimo).toFixed(4)}</span>
                </li>
              ))}
            </ul>
          </div>
          <form onSubmit={submit}>
            <Label htmlFor="pin-gerencia-input" className="text-xs">PIN de Gerencia (4-6 dígitos)</Label>
            <Input
              id="pin-gerencia-input"
              type="password"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              autoFocus
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••"
              className="font-mono text-center tracking-[0.5em] text-lg"
              data-testid="pin-gerencia-input"
            />
            <p className="text-[10px] text-[#5c7368] mt-1 italic">
              Solo un usuario con rol Gerencia o Admin puede autorizar. La acción quedará registrada en auditoría.
            </p>
          </form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#b53a25] hover:bg-[#8e2f1d]" data-testid="pin-gerencia-submit">
            {submitting ? "Validando…" : "Autorizar venta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
